import React, { useEffect, useState } from 'react';
import { Icon, Label, Loader, Message, SemanticCOLORS, Table } from 'semantic-ui-react';
import moment from 'moment-timezone';
import { BatchEvent, BatchEventType, getBatchEvents } from '../../../../services/fire-insurance-batches';

const EVENT_LABELS: Record<BatchEventType, { text: string; color: SemanticCOLORS }> = {
  BATCH_STATUS_CHANGED: { text: 'Status do lote', color: 'grey' },
  TRANSMISSION_REQUESTED: { text: 'Transmissão solicitada', color: 'yellow' },
  BATCH_EDITED: { text: 'Edição', color: 'blue' },
  QUOTE_SYNC: { text: 'Sync da cotação', color: 'teal' },
  TRANSMISSION_BLOCKED: { text: 'Transmissão bloqueada', color: 'orange' },
  PROPOSAL_CREATED: { text: 'Proposta criada', color: 'violet' },
  QUOTE_ROLLED: { text: 'Fatura movimentada', color: 'purple' },
  TRANSMISSION_REGISTERED: { text: 'Transmissão registrada', color: 'green' },
  COMPETENCE_CLOSED_WITHOUT_ITEMS: { text: 'Competência sem itens', color: 'grey' },
  TRANSMISSION_FAILED: { text: 'Falha na transmissão', color: 'red' },
  POST_TRANSMISSION_CHECK: { text: 'Conferência pós-transmissão', color: 'olive' },
};

interface BatchHistoryProps {
  batchId: string;
}

const BatchHistory: React.FC<BatchHistoryProps> = ({ batchId }) => {
  const [events, setEvents] = useState<BatchEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    getBatchEvents(batchId)
      .then((response) => {
        if (active) setEvents(Array.isArray(response) ? response : []);
      })
      .catch(() => {
        if (active) setError('Não foi possível carregar o histórico do lote.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [batchId]);

  if (loading) return <Loader active inline="centered" />;
  if (error) return <Message negative content={error} />;
  if (events.length === 0) {
    return (
      <Message info>
        <Icon name="info circle" />
        Nenhum evento registrado para este lote ainda.
      </Message>
    );
  }

  return (
    <Table celled striped compact>
      <Table.Header>
        <Table.Row>
          <Table.HeaderCell collapsing>Data</Table.HeaderCell>
          <Table.HeaderCell collapsing>Evento</Table.HeaderCell>
          <Table.HeaderCell>Descrição</Table.HeaderCell>
          <Table.HeaderCell collapsing>Cotação</Table.HeaderCell>
          <Table.HeaderCell collapsing>Por</Table.HeaderCell>
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {events.map((event) => {
          const label = EVENT_LABELS[event.eventType] ?? { text: event.eventType, color: 'grey' };
          const reportLink = event.payload?.reportLink as string | undefined;
          return (
            <Table.Row key={event.id}>
              <Table.Cell collapsing>
                {moment(event.createdAt).format('DD/MM/YYYY HH:mm')}
              </Table.Cell>
              <Table.Cell collapsing>
                <Label size="small" color={label.color}>
                  {label.text}
                </Label>
              </Table.Cell>
              <Table.Cell>
                {event.summary}
                {reportLink ? (
                  <>
                    {' '}
                    <a href={reportLink} target="_blank" rel="noopener noreferrer">
                      <Icon name="file excel outline" />
                      relatório
                    </a>
                  </>
                ) : null}
              </Table.Cell>
              <Table.Cell collapsing>{event.quotationCode || '-'}</Table.Cell>
              <Table.Cell collapsing>{event.actorLabel || 'sistema'}</Table.Cell>
            </Table.Row>
          );
        })}
      </Table.Body>
    </Table>
  );
};

export default BatchHistory;
